"use client";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import SIFInvestmentRiskStep from "../processSteps/SIFInvestmentRiskStep";
import SIFInvestmentOverviewStep from "../processSteps/SIFInvestmentOverviewStep";
import SIFInvestmentPortfolioStep from "../processSteps/SIFInvestmentPortfolioStep";
import CustomModal from "./CustomModal";
import Image from "next/image";
import Icon from "../icons/Icons";
import { formatCompactNumber, formatDate } from "@/utils/commonFunctions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { closeSIFDetailsModal } from "@/redux/features/SIFOverviewModal/sifDetailsModalSlice";
import { parseFundSIFSuitability } from "@/utils/sifPageFunctions";
import { addToast } from "../ui/CustomToastManager";
import { useLoginModal } from "@/utils/useLoginModal";
import { apiPost } from "@/utils/apiClient";
import { usePathname, useRouter } from "next/navigation";
import { DASHBOARD_ROOT, ROOT_URL } from "@/utils/gloableVariables";

type TabType = "overview" | "portfolio" | "risk";

export default function SIFInvestmentModal() {
  const dispatch = useDispatch();
  const { isOpen, funds, portfolioData } = useSelector((state: RootState) => state.sifDetailsModal);
  const { isLoggedIn } = useLoginModal();

  const pathName = usePathname();
  const route = useRouter();

  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [isLoading, setIsLoading] = useState(false);
  const meta = parseFundSIFSuitability(funds?.suitability);

  const hasOverview = Boolean(funds);
  const hasPortfolio = Boolean(portfolioData);
  const hasRisk = Boolean(meta || funds);

  const tabs: TabType[] = [hasOverview && "overview", hasPortfolio && "portfolio", hasRisk && "risk"].filter(
    Boolean,
  ) as TabType[];

  useEffect(() => {
    if (!tabs.includes(activeTab)) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab]);

  const overviewData = {
    stats: [
      {
        label: "Returns (3M)",
        value: `${funds?.three_month_abs}%` || "N/A",
        highlightVal: funds?.three_month_abs ?? 0,
      },
      { label: "Min Investment", value: `₹ ${formatCompactNumber(funds?.minimum_investment ?? 0, false, 2, false)}` },
      { label: "Launch Date", value: formatDate(funds?.launch_date, "dd-MM-yyyy") },
      { label: "Lock-in", value: meta?.lock_in_period ?? "None" },
    ],
    description: meta?.investment_strategy ?? "No strategy information available.",
    objective: funds?.investment_obj ?? "No strategy information available.",
  };

  const handleCartAction = async () => {
    if (!isLoggedIn || !funds) return setIsLoading(false);

    setIsLoading(true);

    const payload = {
      scheme_code: funds.scheme_code || "",
      inv_type: "lumpsum",
      amount: String(funds.minimum_investment) ?? "0",
      sip_date: "1",
      offset: "",
      addnl_investment: "",
      folio_no: "",
      source: pathName,
      medium: "Web",
      folio: "",
    };

    try {
      const res = await apiPost("addToCart", payload);

      if (res.status) {
        route.push(`${DASHBOARD_ROOT}single-buy/${res.cart_id}`);
      } else {
        addToast({
          title: "Add To Cart",
          description: "Failed to add to cart!",
          status: "failed",
        });
      }
    } catch (err) {
      console.error(err);
      addToast({
        title: "Add To Cart",
        description: "Something Went Wrong!",
        status: "failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={() => dispatch(closeSIFDetailsModal())}
      isBlurOverlay
      closeOnOverlayClick={false}
      showCloseButton={false}
      maxSize="max-w-194.25"
      className="bg-[#000A1A]! rounded-t-6xl! sm:rounded-6xl! border border-b-0 sm:border-b border-white/30 sm:border-white text-white overflow-hidden"
    >
      <div className="p-4 sm:p-8 pb-0 sm:pb-0">
        {/* Header */}
        <div className="flex items-start xxs:items-center gap-3 mb-6">
          <Image
            src={`${ROOT_URL}img/AMCsLogo/${funds?.amc_code}.png`}
            alt={funds?.scheme_name ?? "Fund Logo"}
            width={50}
            height={50}
            className="w-9 h-9 sm:w-half-px sm:h-half-px shrink-0 object-contain rounded-full"
          />

          <div className="space-y-1">
            <div
              className="xs:text-xl sm:text-2xl xs:leading-1.2! font-bold line-clamp-2"
              title={funds?.scheme_name ?? ""}
            >
              {funds?.scheme_name}
            </div>
            <p className="flex flex-col xxs:flex-row xxs:items-center xxs:gap-1.5 text-sm opacity-60 ">
              <span>{funds?.scheme_category}</span>
              {funds?.asset_size_cr && (
                <>
                  <span className="hidden xxs:inline">•</span>
                  <span>₹ {funds?.asset_size_cr} Cr AUM</span>
                </>
              )}
            </p>
          </div>

          <button
            onClick={() => dispatch(closeSIFDetailsModal())}
            aria-label="Popup Close Icon"
            className="group p-2 ml-auto"
          >
            <Icon.crossIcon className="w-4 h-4 opacity-70 transition group-hover:opacity-100" />
          </button>
        </div>

        {/* Tabs */}
        <div className="relative flex bg-white/5 border border-white/15 p-0.5 rounded-full mb-5">
          {/* Sliding pill */}
          <span
            className={`absolute inset-y-0.5 my-auto rounded-full bg-white/20 shadow-[inset_0.5px_-1px_1px_rgba(255,255,255,0.25),inset_-0.5px_1px_1px_rgba(255,255,255,0.25)] transition-all duration-300 ease-out`}
            style={{
              width: `calc((100% - 4px) / ${tabs.length})`,
              transform: `translateX(${tabs.indexOf(activeTab) * 100}%)`,
            }}
          />

          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative flex-1 py-2 text-xs font-medium capitalize transition duration-300 ${activeTab === tab ? "text-white font-bold" : "text-white/70 hover:text-white"}`}
            >
              {tab === "risk" ? "Risk & Info" : tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -15 }}
            className="h-84 sm:h-87 overflow-y-auto scrollbar-none pb-6"
          >
            {activeTab === "overview" && <SIFInvestmentOverviewStep data={overviewData} />}
            {activeTab === "portfolio" && portfolioData && <SIFInvestmentPortfolioStep portfolioData={portfolioData} />}
            {activeTab === "risk" && funds && <SIFInvestmentRiskStep suitability={meta} fundsData={funds} />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center px-4 xs:px-8 py-4 bg-white/10 border-t border-white">
        <div className="text-white hidden xs:block">
          <p className="text-sm opacity-70">Minimum Investment</p>
          <strong className="text-xl font-bold mt-1">
            ₹ {formatCompactNumber(funds?.minimum_investment ?? 0, false, 2, false)}
          </strong>
        </div>

        <div className="w-full xs:w-fit flex gap-2">
          <button
            onClick={() => dispatch(closeSIFDetailsModal())}
            className="px-8 py-2 font-bold text-sm border border-white rounded-xl transition hover:bg-white/20"
          >
            Close
          </button>

          <button
            onClick={handleCartAction}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 px-8 py-2 font-bold text-sm border border-white rounded-xl bg-white text-black transition hover:bg-transparent hover:text-white"
          >
            Invest Now
            {isLoading && <Icon.buttonLoaderSecondary className="w-4 h-4" />}
            {!isLoading && <Icon.rightArrow className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </CustomModal>
  );
}
